<template>
	<aside class="db2-menu" :class="{ 'is-dash-dragging': !!dragId }">
		<HaqTextField v-model="search" placeholder="Search dashboards" append-icon="mdi-magnify" hide-details />
		<div class="db2-menu-scroll">
			<div
				class="db2-menu-folder"
				v-for="folder in visibleFolders"
				:key="folder.objectId"
				:class="{ 'is-drop-target': isFolderDropTarget(folder.objectId) }"
				@dragenter.prevent="onFolderDragOver($event, folder.objectId)"
				@dragover.prevent="onFolderDragOver($event, folder.objectId)"
				@drop.prevent="onFolderDrop($event, folder.objectId)"
			>
				<div class="db2-menu-folder-row">
					<button type="button" class="db2-menu-folder-head" @click="toggleFolder(folder.objectId)">
						<v-icon small>{{ folderIcon(folder) }}</v-icon>
						<span>{{ folder.name }}</span>
					</button>
					<button
						v-if="canEdit"
						type="button"
						class="db2-menu-folder-dots"
						aria-label="Folder actions"
						@click.stop="openFolderMenu($event, folder)"
					>
						<v-icon small>mdi-dots-vertical</v-icon>
					</button>
				</div>
				<div class="db2-menu-folder-body" v-show="expanded[folder.objectId] !== false">
					<div
						v-for="d in dashboardsIn(folder.objectId)"
						:key="d.objectId"
						class="db2-menu-dash-row"
					>
						<button
							type="button"
							class="db2-menu-item nested"
							:class="itemClass(d)"
							:disabled="isItemLoading(d)"
							:draggable="canDragItem(d) ? 'true' : 'false'"
							:aria-busy="isItemLoading(d) ? 'true' : 'false'"
							@click="selectDashboard(d)"
							@dragstart="onDashDragStart($event, d)"
							@dragend="onDashDragEnd"
						>
							<v-icon small :class="{ 'db2-menu-spin': isItemLoading(d) }">{{ itemIcon(d) }}</v-icon>
							<span>{{ d.name }}</span>
						</button>
						<button
							v-if="canEdit"
							type="button"
							class="db2-menu-folder-dots"
							aria-label="Dashboard actions"
							@click.stop="openDashMenu($event, d)"
						>
							<v-icon small>mdi-dots-vertical</v-icon>
						</button>
					</div>
				</div>
			</div>
			<div
				class="db2-menu-group"
				v-if="ungrouped.length || dragId"
				:class="{ 'is-drop-target': isUngroupedDropTarget, 'is-drop-ready': !!dragId }"
				@dragenter.prevent="onUngroupedDragOver"
				@dragover.prevent="onUngroupedDragOver"
				@drop.prevent="onUngroupedDrop"
			>
				<div
					v-for="d in ungrouped"
					:key="d.objectId"
					class="db2-menu-dash-row"
				>
					<button
						type="button"
						class="db2-menu-item"
						:class="itemClass(d)"
						:disabled="isItemLoading(d)"
						:draggable="canDragItem(d) ? 'true' : 'false'"
						:aria-busy="isItemLoading(d) ? 'true' : 'false'"
						@click="selectDashboard(d)"
						@dragstart="onDashDragStart($event, d)"
						@dragend="onDashDragEnd"
					>
						<v-icon small :class="{ 'db2-menu-spin': isItemLoading(d) }">{{ itemIcon(d) }}</v-icon>
						<span>{{ d.name }}</span>
					</button>
					<button
						v-if="canEdit"
						type="button"
						class="db2-menu-folder-dots"
						aria-label="Dashboard actions"
						@click.stop="openDashMenu($event, d)"
					>
						<v-icon small>mdi-dots-vertical</v-icon>
					</button>
				</div>
			</div>
			<button v-if="canEdit" type="button" class="db2-menu-new" @click="newDashboard">
				<span class="db2-menu-dash-plus">
					<v-icon small>mdi-view-dashboard</v-icon>
					<v-icon class="plus">mdi-plus</v-icon>
				</span>
				<span>New dashboard</span>
			</button>
			<button v-if="canEdit" type="button" class="db2-menu-new" @click="newFolder">
				<v-icon small>mdi-folder-plus-outline</v-icon>
				<span>New folder</span>
			</button>
		</div>
		<div class="db2-quick">
			<div class="db2-quick-title">Quick Actions</div>
			<span class="db2-quick-wrap" title="Coming soon">
				<button type="button" class="db2-quick-btn" disabled>
					<v-icon small>mdi-clipboard-text-outline</v-icon>
					<span>Create Work Order</span>
					<v-icon small class="db2-quick-chevron">mdi-chevron-right</v-icon>
				</button>
			</span>
			<span class="db2-quick-wrap" title="Coming soon">
				<button type="button" class="db2-quick-btn" disabled>
					<v-icon small>mdi-calendar-clock</v-icon>
					<span>Schedule Maintenance</span>
					<v-icon small class="db2-quick-chevron">mdi-chevron-right</v-icon>
				</button>
			</span>
			<span class="db2-quick-wrap" title="Coming soon">
				<button type="button" class="db2-quick-btn" disabled>
					<v-icon small>mdi-plus-circle-outline</v-icon>
					<span>Add Asset</span>
					<v-icon small class="db2-quick-chevron">mdi-chevron-right</v-icon>
				</button>
			</span>
			<span class="db2-quick-wrap" title="Coming soon">
				<button type="button" class="db2-quick-btn" disabled>
					<v-icon small>mdi-upload-outline</v-icon>
					<span>Upload Document</span>
					<v-icon small class="db2-quick-chevron">mdi-chevron-right</v-icon>
				</button>
			</span>
		</div>
		<v-menu v-model="ctx.open" :position-x="ctx.x" :position-y="ctx.y" absolute offset-y>
			<v-list dense>
				<template v-if="ctx.kind === 'dash'">
					<v-list-item @click="renameDash"><v-list-item-title>Rename</v-list-item-title></v-list-item>
					<v-list-item @click="confirmDeleteDash"><v-list-item-title>Delete</v-list-item-title></v-list-item>
				</template>
				<template v-else-if="ctx.kind === 'folder'">
					<v-list-item @click="renameFolder"><v-list-item-title>Rename folder</v-list-item-title></v-list-item>
					<v-list-item @click="confirmDeleteFolder"><v-list-item-title>Delete folder</v-list-item-title></v-list-item>
				</template>
			</v-list>
		</v-menu>
		<v-dialog v-model="folderOpen" content-class="haq-dialog" max-width="480px" persistent>
			<v-card class="haq-dialog-card">
				<v-card-title class="haq-dialog-card-header">
					<h5>{{ folderMode === 'rename' ? 'Rename folder' : 'New folder' }}</h5>
					<v-spacer />
					<v-icon class="close-icon" @click="closeFolderDialog">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					<HaqTextField
						v-model="folderName"
						label="Folder name"
						hide-details
						:disabled="folderSaving"
						@keyup.enter="submitFolder"
					/>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn class="haq-btn haq-btn-secondary" :disabled="folderSaving" @click="closeFolderDialog">Cancel</v-btn>
					<v-btn
						class="haq-btn haq-btn-primary"
						:loading="folderSaving"
						:disabled="!folderNameTrimmed || folderSaving"
						@click="submitFolder"
					>{{ folderMode === 'rename' ? 'Save' : 'Create' }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="deleteFolderOpen" content-class="haq-dialog" max-width="480px" persistent>
			<v-card class="haq-dialog-card">
				<v-card-title class="haq-dialog-card-header">
					<h5>Delete folder</h5>
					<v-spacer />
					<v-icon class="close-icon" @click="closeDeleteFolder">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					Delete “{{ deleteFolderName }}”? Dashboards in this folder will become ungrouped.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn class="haq-btn haq-btn-secondary" :disabled="deleteFolderSaving" @click="closeDeleteFolder">Cancel</v-btn>
					<v-btn
						class="haq-btn haq-btn-danger"
						:loading="deleteFolderSaving"
						:disabled="deleteFolderSaving"
						@click="submitDeleteFolder"
					>Delete</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="dashRenameOpen" content-class="haq-dialog" max-width="480px" persistent>
			<v-card class="haq-dialog-card">
				<v-card-title class="haq-dialog-card-header">
					<h5>Rename dashboard</h5>
					<v-spacer />
					<v-icon class="close-icon" @click="closeDashRename">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					<HaqTextField
						v-model="dashName"
						label="Dashboard name"
						hide-details
						:disabled="dashRenameSaving"
						@keyup.enter="submitDashRename"
					/>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn class="haq-btn haq-btn-secondary" :disabled="dashRenameSaving" @click="closeDashRename">Cancel</v-btn>
					<v-btn
						class="haq-btn haq-btn-primary"
						:loading="dashRenameSaving"
						:disabled="!dashNameTrimmed || dashRenameSaving"
						@click="submitDashRename"
					>Save</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="deleteDashOpen" content-class="haq-dialog" max-width="480px" persistent>
			<v-card class="haq-dialog-card">
				<v-card-title class="haq-dialog-card-header">
					<h5>Delete dashboard</h5>
					<v-spacer />
					<v-icon class="close-icon" @click="closeDeleteDash">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					Delete “{{ deleteDashName }}”? This cannot be undone.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn class="haq-btn haq-btn-secondary" :disabled="deleteDashSaving" @click="closeDeleteDash">Cancel</v-btn>
					<v-btn
						class="haq-btn haq-btn-danger"
						:loading="deleteDashSaving"
						:disabled="deleteDashSaving"
						@click="submitDeleteDash"
					>Delete</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</aside>
</template>

<script>
import HaqTextField from '@/components/form/HaqTextField.vue';

/**
 * Left dashboard list with search, folders, and disabled quick actions.
 */
export default {
	name: 'DashboardMenu',
	components: { HaqTextField },
	props: {
		dashboards: { type: Array, default: () => [] },
		folders: { type: Array, default: () => [] },
		activeId: { type: String, default: null },
		loadingId: { type: String, default: null },
		canEdit: { type: Boolean, default: false }
	},
	data() {
		return {
			search: '',
			expanded: {},
			ctx: { open: false, x: 0, y: 0, kind: null, target: null },
			folderOpen: false,
			folderSaving: false,
			folderMode: 'create',
			folderName: '',
			folderEditId: null,
			deleteFolderOpen: false,
			deleteFolderSaving: false,
			deleteFolderTarget: null,
			dashRenameOpen: false,
			dashRenameSaving: false,
			dashName: '',
			dashEditId: null,
			deleteDashOpen: false,
			deleteDashSaving: false,
			deleteDashTarget: null,
			dragId: null,
			dropFolderId: null,
			dropAt: 0
		};
	},
	computed: {
		/**
		 * Search needle.
		 * @returns {string}
		 */
		q() {
			return (this.search || '').trim().toLowerCase();
		},
		/**
		 * Dashboards matching search.
		 * @returns {Array}
		 */
		filtered() {
			if (!this.q) return this.dashboards || [];
			return (this.dashboards || []).filter(d => (d.name || '').toLowerCase().includes(this.q));
		},
		/**
		 * Ungrouped dashboards.
		 * @returns {Array}
		 */
		ungrouped() {
			return this.filtered.filter(d => !d.folderId);
		},
		/**
		 * Folders that match search or contain a match.
		 * @returns {Array}
		 */
		visibleFolders() {
			const folders = this.folders || [];
			if (!this.q) return folders;
			return folders.filter(f => {
				if ((f.name || '').toLowerCase().includes(this.q)) return true;
				return this.dashboardsIn(f.objectId).length > 0;
			});
		},
		/**
		 * Trimmed folder name for the create/rename dialog.
		 * @returns {string}
		 */
		folderNameTrimmed() {
			return String(this.folderName || '').trim();
		},
		/**
		 * Name shown in the delete-folder confirm copy.
		 * @returns {string}
		 */
		deleteFolderName() {
			return (this.deleteFolderTarget && this.deleteFolderTarget.name) || '';
		},
		/**
		 * Trimmed dashboard name for the rename dialog.
		 * @returns {string}
		 */
		dashNameTrimmed() {
			return String(this.dashName || '').trim();
		},
		/**
		 * Name shown in the delete-dashboard confirm copy.
		 * @returns {string}
		 */
		deleteDashName() {
			return (this.deleteDashTarget && this.deleteDashTarget.name) || '';
		},
		/**
		 * True while a dashboard is dragged over the ungrouped list.
		 * @returns {boolean}
		 */
		isUngroupedDropTarget() {
			return !!this.dragId && this.dropFolderId === '';
		}
	},
	methods: {
		/**
		 * True while this dashboard is fetching after a click.
		 * @param {object} d
		 * @returns {boolean}
		 */
		isItemLoading(d) {
			return !!(this.loadingId && d && d.objectId === this.loadingId);
		},
		/**
		 * Selected or in-flight menu item classes.
		 * @param {object} d
		 * @returns {{ on: boolean, 'is-loading': boolean }}
		 */
		itemClass(d) {
			const loading = this.isItemLoading(d);
			return {
				on: loading || (d && d.objectId === this.activeId),
				'is-loading': loading,
				'is-dragging': !!(this.dragId && d && d.objectId === this.dragId)
			};
		},
		/**
		 * Spinner while loading, otherwise the dashboard glyph.
		 * @param {object} d
		 * @returns {string}
		 */
		itemIcon(d) {
			if (this.isItemLoading(d)) return 'mdi-loading';
			return (d && d.icon) || 'mdi-view-dashboard';
		},
		/**
		 * Open a dashboard unless it is already active or loading.
		 * @param {object} d
		 */
		selectDashboard(d) {
			if (Date.now() - this.dropAt < 500) return;
			const id = d && d.objectId;
			if (!id || id === this.activeId || id === this.loadingId) return;
			this.$emit('select-dashboard', id);
		},
		/**
		 * True when this dashboard can start a folder drag.
		 * @param {object} d
		 * @returns {boolean}
		 */
		canDragItem(d) {
			return !!(this.canEdit && d && d.objectId && !this.isItemLoading(d));
		},
		/**
		 * True while a dashboard is dragged over this folder.
		 * @param {string} folderId
		 * @returns {boolean}
		 */
		isFolderDropTarget(folderId) {
			return !!this.dragId && this.dropFolderId === folderId;
		},
		/**
		 * Dashboards in a folder (search-aware).
		 * @param {string} folderId
		 * @returns {Array}
		 */
		dashboardsIn(folderId) {
			return this.filtered.filter(d => d.folderId === folderId);
		},
		/**
		 * Folder glyph: closed when collapsed, open when expanded.
		 * @param {object} folder
		 * @returns {string}
		 */
		folderIcon(folder) {
			const open = !folder || this.expanded[folder.objectId] !== false;
			if (open) return 'mdi-folder-open';
			return (folder && folder.icon) || 'mdi-folder';
		},
		/**
		 * Await a parent listener. Vue 2 $emit is not awaitable.
		 * @param {string} event
		 * @param {*} payload
		 * @returns {Promise<*>}
		 */
		callParent(event, payload) {
			const fn = this.$listeners[event];
			if (Array.isArray(fn)) {
				return Promise.all(fn.map(handler => handler(payload)));
			}
			if (typeof fn === 'function') return fn(payload);
			this.$emit(event, payload);
			return Promise.resolve();
		},
		/**
		 * Toggle folder expand.
		 * @param {string} id
		 */
		toggleFolder(id) {
			if (Date.now() - this.dropAt < 500) return;
			this.$set(this.expanded, id, this.expanded[id] === false);
		},
		/**
		 * Dashboard id from a drop event (or the in-flight drag).
		 * @param {DragEvent} e
		 * @returns {string}
		 */
		readDragId(e) {
			const fromData = e && e.dataTransfer && e.dataTransfer.getData('text/plain');
			return (fromData || this.dragId || '').trim();
		},
		/**
		 * Clear drag highlight state.
		 */
		clearDrag() {
			this.dragId = null;
			this.dropFolderId = null;
		},
		/**
		 * Start dragging a dashboard to a folder.
		 * @param {DragEvent} e
		 * @param {object} d
		 */
		onDashDragStart(e, d) {
			if (!this.canDragItem(d) || !e || !e.dataTransfer) {
				if (e && e.preventDefault) e.preventDefault();
				return;
			}
			e.dataTransfer.setData('text/plain', d.objectId);
			e.dataTransfer.effectAllowed = 'move';
			this.dragId = d.objectId;
			this.dropFolderId = null;
		},
		/**
		 * End a dashboard drag.
		 */
		onDashDragEnd() {
			this.clearDrag();
		},
		/**
		 * Highlight a folder while a dashboard is dragged over it.
		 * @param {DragEvent} e
		 * @param {string} folderId
		 */
		onFolderDragOver(e, folderId) {
			if (!this.dragId) return;
			if (e && e.dataTransfer) e.dataTransfer.dropEffect = 'move';
			this.dropFolderId = folderId;
			if (this.expanded[folderId] === false) this.$set(this.expanded, folderId, true);
		},
		/**
		 * Drop a dashboard into a folder.
		 * @param {DragEvent} e
		 * @param {string} folderId
		 */
		onFolderDrop(e, folderId) {
			this.dropAt = Date.now();
			const id = this.readDragId(e);
			this.clearDrag();
			this.moveDashboardToFolder(id, folderId);
		},
		/**
		 * Highlight the ungrouped list while a dashboard is dragged over it.
		 * @param {DragEvent} e
		 */
		onUngroupedDragOver(e) {
			if (!this.dragId) return;
			if (e && e.dataTransfer) e.dataTransfer.dropEffect = 'move';
			this.dropFolderId = '';
		},
		/**
		 * Drop a dashboard out of its folder.
		 * @param {DragEvent} e
		 */
		onUngroupedDrop(e) {
			this.dropAt = Date.now();
			const id = this.readDragId(e);
			this.clearDrag();
			this.moveDashboardToFolder(id, '');
		},
		/**
		 * Persist a dashboard folder move.
		 * @param {string} dashboardId
		 * @param {string} folderId
		 */
		async moveDashboardToFolder(dashboardId, folderId) {
			if (!dashboardId || !this.canEdit) return;
			const d = (this.dashboards || []).find(row => row.objectId === dashboardId);
			if (!d) return;
			const next = folderId || '';
			const cur = d.folderId || '';
			if (next === cur) return;
			try {
				await this.callParent('update-dashboard', {
					dashboardId,
					updateData: { folderId: next }
				});
			} catch (err) {
				console.error(err);
			}
		},
		/**
		 * Open dashboard actions from the dots button, anchored to that button.
		 * @param {MouseEvent} e
		 * @param {object} d
		 */
		openDashMenu(e, d) {
			if (!this.canEdit) return;
			e.preventDefault();
			e.stopPropagation();
			const rect = e.currentTarget && e.currentTarget.getBoundingClientRect
				? e.currentTarget.getBoundingClientRect()
				: { left: e.clientX, bottom: e.clientY };
			this.ctx = { open: false, x: rect.left, y: rect.bottom, kind: 'dash', target: d };
			this.$nextTick(() => {
				this.ctx.open = true;
			});
		},
		/**
		 * Open folder context menu from the dots button, anchored to that button.
		 * @param {MouseEvent} e
		 * @param {object} f
		 */
		openFolderMenu(e, f) {
			if (!this.canEdit) return;
			e.preventDefault();
			e.stopPropagation();
			const rect = e.currentTarget && e.currentTarget.getBoundingClientRect
				? e.currentTarget.getBoundingClientRect()
				: { left: e.clientX, bottom: e.clientY };
			this.ctx = { open: false, x: rect.left, y: rect.bottom, kind: 'folder', target: f };
			this.$nextTick(() => {
				this.ctx.open = true;
			});
		},
		/**
		 * Open the create-dashboard flow.
		 */
		newDashboard() {
			this.$emit('create-dashboard');
		},
		/**
		 * Open the create-folder dialog.
		 */
		newFolder() {
			this.folderMode = 'create';
			this.folderEditId = null;
			this.folderName = '';
			this.folderOpen = true;
		},
		/**
		 * Close the create/rename folder dialog unless a save is in flight.
		 */
		closeFolderDialog() {
			if (this.folderSaving) return;
			this.folderOpen = false;
			this.folderName = '';
			this.folderMode = 'create';
			this.folderEditId = null;
		},
		/**
		 * Create or rename the folder from the dialog name.
		 */
		async submitFolder() {
			const name = this.folderNameTrimmed;
			if (!name || this.folderSaving) return;
			this.folderSaving = true;
			try {
				if (this.folderMode === 'rename' && this.folderEditId) {
					await this.callParent('rename-folder', { folderId: this.folderEditId, name });
				} else {
					await this.callParent('create-folder', name);
				}
				this.folderSaving = false;
				this.closeFolderDialog();
			} catch (e) {
				this.folderSaving = false;
			}
		},
		/**
		 * Open the rename-dashboard dialog.
		 */
		renameDash() {
			const d = this.ctx.target;
			this.ctx.open = false;
			if (!d) return;
			this.dashEditId = d.objectId;
			this.dashName = d.name || '';
			this.dashRenameOpen = true;
		},
		/**
		 * Close the rename-dashboard dialog unless a save is in flight.
		 */
		closeDashRename() {
			if (this.dashRenameSaving) return;
			this.dashRenameOpen = false;
			this.dashName = '';
			this.dashEditId = null;
		},
		/**
		 * Save the renamed dashboard.
		 */
		async submitDashRename() {
			const name = this.dashNameTrimmed;
			if (!name || !this.dashEditId || this.dashRenameSaving) return;
			this.dashRenameSaving = true;
			try {
				await this.callParent('rename-dashboard', { dashboardId: this.dashEditId, name: name });
				this.dashRenameSaving = false;
				this.closeDashRename();
			} catch (e) {
				this.dashRenameSaving = false;
			}
		},
		/**
		 * Open the delete-dashboard confirm dialog.
		 */
		confirmDeleteDash() {
			const d = this.ctx.target;
			this.ctx.open = false;
			if (!d) return;
			this.deleteDashTarget = d;
			this.deleteDashOpen = true;
		},
		/**
		 * Close the delete-dashboard dialog unless a delete is in flight.
		 */
		closeDeleteDash() {
			if (this.deleteDashSaving) return;
			this.deleteDashOpen = false;
			this.deleteDashTarget = null;
		},
		/**
		 * Delete the confirmed dashboard.
		 */
		async submitDeleteDash() {
			const d = this.deleteDashTarget;
			if (!d || this.deleteDashSaving) return;
			this.deleteDashSaving = true;
			try {
				await this.callParent('delete-dashboard', d.objectId);
				this.deleteDashSaving = false;
				this.closeDeleteDash();
			} catch (e) {
				this.deleteDashSaving = false;
			}
		},
		/**
		 * Open the rename-folder dialog.
		 */
		renameFolder() {
			const f = this.ctx.target;
			this.ctx.open = false;
			if (!f) return;
			this.folderMode = 'rename';
			this.folderEditId = f.objectId;
			this.folderName = f.name || '';
			this.folderOpen = true;
		},
		/**
		 * Open the delete-folder confirm dialog.
		 */
		confirmDeleteFolder() {
			const f = this.ctx.target;
			this.ctx.open = false;
			if (!f) return;
			this.deleteFolderTarget = f;
			this.deleteFolderOpen = true;
		},
		/**
		 * Close the delete-folder dialog unless a delete is in flight.
		 */
		closeDeleteFolder() {
			if (this.deleteFolderSaving) return;
			this.deleteFolderOpen = false;
			this.deleteFolderTarget = null;
		},
		/**
		 * Delete the confirmed folder.
		 */
		async submitDeleteFolder() {
			const f = this.deleteFolderTarget;
			if (!f || this.deleteFolderSaving) return;
			this.deleteFolderSaving = true;
			try {
				await this.callParent('delete-folder', f.objectId);
				this.deleteFolderSaving = false;
				this.closeDeleteFolder();
			} catch (e) {
				this.deleteFolderSaving = false;
			}
		}
	}
};
</script>
